<script setup lang="ts">
import { SpeckleConfigurationType } from "@/features/BuildingProject";
import NewSpeckleChild from "@/types/Views/MappingWizard/NewSpeckleChild";
import { DeclaredUnitEnum } from "@/types/Views/MaterialDatabase/MaterialEnums";

import TextInput from "@/components/Reusables/Inputs/TextInput.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import DeclaredUnitSelect from "./DeclaredUnitSelect.vue";
import Switch from "@/components/Reusables/Switches/Switch.vue";
import { Trash2 } from "@lucide/vue";
import { computed, watch } from "vue";

const props = defineProps<{
  child: NewSpeckleChild;
  byType: boolean;
  index: number;
  declaredUnitEnums: { [key: string]: DeclaredUnitEnum };
  hasValidated: boolean;
  speckleConfiguration: SpeckleConfigurationType;
}>();

const emits = defineEmits<{
  onRemove: [];
  formInvalid: [invalid: boolean];
  onRecycle: [isRecycled: boolean];
}>();

async function updateIsRecycledValue(newIsRecycled: boolean) {
  emits("onRecycle", newIsRecycled);
}

const validateUnitValue = computed(() => {
  const value = props.child.declaredUnitValuePairs[0]?.value;

  if (value === null || value === undefined || value < 0) {
    return "Please enter a valid unit value";
  }

  return undefined;
});

const validateIdentifier = computed(() => {
  const value = props.child.materialIdentifier.length === 0;

  if (value) {
    return "Please enter a name";
  }

  return undefined;
});

watch(
  validateUnitValue,
  (errorMsg) => {
    if (errorMsg) {
      emits("formInvalid", true);
    } else {
      emits("formInvalid", false);
    }
  },
  { immediate: true }
);

watch(
  validateIdentifier,
  (errorMsg) => {
    if (errorMsg) {
      emits("formInvalid", true);
    } else {
      emits("formInvalid", false);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="gap-px-8 grid grid-cols-22 px-4 py-3">
    <div class="col-span-7">
      <TextInput
        v-model:value="child.materialIdentifier"
        color="primary"
        placeholder="Material name"
        :error-msg="hasValidated ? validateIdentifier : ''" />
    </div>

    <div class="col-span-6">
      <DeclaredUnitSelect
        :declared-unit-enums="declaredUnitEnums"
        :material="child"
        :error-msg="hasValidated && child.declaredUnitValuePairs[0].declaredUnit === -1 ? ' ' : undefined" />
    </div>

    <div class="col-span-5">
      <NumberInput
        v-model:value="child.declaredUnitValuePairs[0].value"
        color="primary"
        placeholder="Value"
        :error-msg="hasValidated ? validateUnitValue : ''" />
    </div>
    <div class="col-span-2 flex items-center justify-center">
      <Switch
        color="primary"
        :value="child?.isRecycled || false"
        aria-label="Reuse material"
        @update:value="(value: boolean) => updateIsRecycledValue(value)" />
    </div>

    <div class="col-span-1">
      <div class="flex h-full items-center justify-center">
        <button
          v-if="index > 0"
          v-tooltip="{ text: 'Remove material', variant: 'label', placement: 'top' }"
          type="button"
          class="focus-ring interactive-active duration-fast ease-standard rounded-4 text-type-muted hover:bg-app-red-50 hover:text-app-red-600 p-1 transition-colors"
          aria-label="Remove material"
          @click="emits('onRemove')">
          <Trash2
            class="icon-sm"
            :stroke-width="1.5"
            aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
